import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listPrintProviders, pingPrintProvider, setPrintProviderEnabled } from '../../../services/adminApi';
import { adminPath } from '../shell/adminNav';
import { Switch, Tag } from './ui';
import './catalog.css';

// Fournisseurs d'impression (Gelato, Prodigi) : une carte par fournisseur avec son etat. La cle d'API n'est JAMAIS affichee (ni en
// entier ni en partie) ni stockee en base : elle vient de l'environnement du serveur, on dit seulement si elle est renseignee.
// Le mode test / reel n'est pas un interrupteur par fournisseur : il SUIT le mode du site (Mode du site), qu'on ne fait que rappeler.

function Carte({ fournisseur, onChange }) {
  const { t, i18n } = useTranslation('admin');
  const [test, setTest] = useState(null); // { ok, ms, message } | 'en cours'
  const [erreur, setErreur] = useState('');
  const [bascule, setBascule] = useState(false);

  const date = (valeur) => new Date(valeur).toLocaleString(i18n.language);
  const verrouille = fournisseur.id === 'gelato';

  const tester = async () => {
    setTest('en cours');
    setErreur('');
    try {
      setTest(await pingPrintProvider(fournisseur.id));
      onChange();
    } catch (err) {
      setTest(null);
      setErreur(err?.message || t('catalog.providers.testFailed'));
    }
  };

  const basculer = async (valeur) => {
    setBascule(true);
    setErreur('');
    try {
      await setPrintProviderEnabled(fournisseur.id, valeur);
      onChange();
    } catch (err) {
      setErreur(err?.message || t('catalog.providers.toggleFailed'));
    } finally {
      setBascule(false);
    }
  };

  let etat = <Tag tone="mute">{t('catalog.providers.off')}</Tag>;
  if (!fournisseur.adapterInstalled) etat = <Tag tone="warn">{t('catalog.providers.adapterMissing')}</Tag>;
  else if (fournisseur.ready === false) etat = <Tag tone="warn">{t('catalog.providers.notReady')}</Tag>;
  else if (fournisseur.enabled && fournisseur.configured) etat = <Tag tone="ok">{t('catalog.providers.connected')}</Tag>;
  else if (!fournisseur.configured) etat = <Tag tone="warn">{t('catalog.providers.toConfigure')}</Tag>;

  return (
    <section className="adm-card adm-prov" aria-labelledby={`prov-${fournisseur.id}`}>
      <div className="adm-prov-row">
        <h2 id={`prov-${fournisseur.id}`}>{fournisseur.label}</h2>
        {etat}
        <span className="adm-sp" />
        <label className="adm-prov-switch">
          <span>{fournisseur.enabled ? t('catalog.providers.enabled') : t('catalog.providers.disabled')}</span>
          <Switch
            checked={fournisseur.enabled}
            onChange={basculer}
            label={t('catalog.providers.switchLabel', { name: fournisseur.label })}
            disabled={verrouille || bascule}
          />
        </label>
      </div>

      <dl className="adm-kv">
        <dt>{t('catalog.providers.apiKey')}</dt>
        <dd>
          {fournisseur.configured ? <Tag tone="ok">{t('catalog.providers.keyConfigured')}</Tag> : <Tag tone="warn">{t('catalog.providers.keyMissing')}</Tag>}
          <span className="adm-note"> {t('catalog.providers.keyEnv')}</span>
        </dd>
        <dt>{t('catalog.providers.mode')}</dt>
        <dd>
          {fournisseur.mode ? <Tag tone={fournisseur.mode === 'production' ? 'bad' : 'warn'}>{t(fournisseur.mode === 'production' ? 'catalog.providers.modeProduction' : 'catalog.providers.modeTest')}</Tag> : '—'}
          <span className="adm-note"> <Link to={adminPath('mode')}>{t('catalog.providers.modeFollows')}</Link></span>
        </dd>
        <dt>{t('catalog.providers.lastCall')}</dt>
        <dd>{fournisseur.lastCall ? `${date(fournisseur.lastCall.at)}${fournisseur.lastCall.label ? ` · ${fournisseur.lastCall.label}` : ''}` : t('catalog.providers.never')}</dd>
        <dt>{t('catalog.providers.lastEvent')}</dt>
        <dd>{fournisseur.lastEvent ? `${date(fournisseur.lastEvent.at)}${fournisseur.lastEvent.label ? ` · ${fournisseur.lastEvent.label}` : ''}` : t('catalog.providers.never')}</dd>
        <dt>{t('catalog.providers.activeProducts')}</dt>
        <dd>{fournisseur.activeProductCount} / {fournisseur.productCount}</dd>
      </dl>

      {fournisseur.notReadyReason && <p className="adm-note">{fournisseur.notReadyReason}</p>}

      <div className="adm-actions">
        {fournisseur.supportsPing && (
          <button type="button" className="btn btn-outline btn-sm" onClick={tester} disabled={test === 'en cours'}>
            {test === 'en cours' ? t('catalog.providers.testing') : t('catalog.providers.test')}
          </button>
        )}
        <Link className="btn btn-outline btn-sm" to={adminPath('products')}>{t('catalog.providers.seeProducts')}</Link>
      </div>
      <p className="adm-note" role="status" aria-live="polite">
        {test && test !== 'en cours' && (test.ok
          ? t('catalog.providers.testOk', { ms: test.ms })
          : t('catalog.providers.testFailedWith', { message: test.message }))}
        {!test && fournisseur.lastPing && (fournisseur.lastPing.ok
          ? t('catalog.providers.lastTestOk', { date: date(fournisseur.lastPing.at) })
          : t('catalog.providers.lastTestFailed', { date: date(fournisseur.lastPing.at), message: fournisseur.lastPing.message || '' }))}
      </p>
      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
    </section>
  );
}

export default function ProvidersSection() {
  const { t } = useTranslation('admin');
  const [fournisseurs, setFournisseurs] = useState(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    try {
      setFournisseurs((await listPrintProviders()).providers);
      setErreur('');
    } catch (err) {
      setErreur(err?.message || t('catalog.providers.loadFailed'));
    }
  }, [t]);

  useEffect(() => { charger(); }, [charger]);

  if (erreur && !fournisseurs) return <p className="adm-error" role="alert">{erreur}</p>;
  if (!fournisseurs) return <p className="adm-state" role="status">{t('catalog.common.loading')}</p>;

  return (
    <>
      <div className="adm-cols-eq">
        {fournisseurs.map((f) => <Carte key={f.id} fournisseur={f} onChange={charger} />)}
      </div>
      <section className="adm-card">
        <h2>{t('catalog.providers.routingTitle')}</h2>
        <p className="adm-note">{t('catalog.providers.routingBody')}</p>
      </section>
    </>
  );
}
